import React, { CSSProperties } from "react";
import {
  IEntityMinimalModel,
  IdTypes,
  StringIndexedDict,
  IGenericRequestParameters,
  InferSortByOptions,
  EntityOrderBy,
} from "../../../api/GenericTypes";
import { FloatingDiv } from "../FloatingDiv/FloatingDiv";
import { Placement } from "@floating-ui/react";
import { LucideIcon } from "../../icon/LucideIcon";
// import { MoreDropdown } from "../MoreDropdown/MoreDropdown";

interface OrderBySelectDropdownProps<
  Entity extends IEntityMinimalModel<IdTypes>,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
> {
  btnCls?: string;
  orderByOptions: readonly InferSortByOptions<Filters>[];
  currentFilters: Filters;
  setCurrentFilters: React.Dispatch<React.SetStateAction<Filters>>;
  drop?: Placement;
  listContainerStyle?: CSSProperties;
}

const defaultDropdownStyles: CSSProperties = {
  backgroundColor: "var(--white)",
  listStyleType: "none",
  border: "1px solid var(--gray-200)",
  boxShadow: "0 0 5px 0 var(--gray-200)",
  maxHeight: "193px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "1px",
  padding: "1px",
};

export const OrderBySelectDropdown = <
  Entity extends IEntityMinimalModel<IdTypes>,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  btnCls = "btn btn-sm btn-default",
  orderByOptions,
  currentFilters,
  setCurrentFilters,
  drop = "bottom-end",
  listContainerStyle = defaultDropdownStyles,
}: OrderBySelectDropdownProps<Entity, Filters>) => {
  const options = Array.from(new Set(orderByOptions.filter((opt) => opt !== "CUSTOM_FIELD")));

  const currentOrderBy: EntityOrderBy<InferSortByOptions<Filters>>["property"] | undefined =
    currentFilters.sortBy?.[0]?.property || undefined;
  const currentOrderByDirection: EntityOrderBy<InferSortByOptions<Filters>>["sortDirection"] | undefined =
    currentFilters.sortBy?.[0]?.sortDirection || undefined;

  return (
    <FloatingDiv
      placement={drop}
      triggerComponent={({ setOpen, ref, getReferenceProps }) => {
        return (
          <div className="flex btn-group btn-group" style={{ height: "100%" }}>
            <button
              className={`${btnCls} dropdown-toggle`}
              ref={ref}
              {...getReferenceProps()}
              onClick={() => setOpen((prevState) => !prevState)}
              title="Sorting options"
            >
              {(currentOrderBy as string) || "Sort by"}
            </button>
            <button
              className={`flex align-center ${btnCls} dropdown-toggle`}
              onClick={() => {
                setCurrentFilters((prev) => ({
                  ...prev,
                  sortBy: [
                    {
                      property: currentOrderBy,
                      sortDirection: currentOrderByDirection === "ASC" ? "DESC" : "ASC",
                    },
                  ],
                }));
              }}
              title="Sort direction"
              disabled={!currentOrderBy}
            >
              {currentOrderByDirection === "DESC" ? (
                <LucideIcon name="chevron-down" />
              ) : (
                <LucideIcon name="chevron-up" />
              )}
              <span className="sr-only">Sort direction</span>
            </button>
          </div>
        );
      }}
    >
      {({ open }) => (
        <div style={listContainerStyle} className={`${open ? "focus" : ""}`}>
          {options?.map((opt, index) => (
            <li key={index}>
              <button
                className={`btn btn-ghost-secondary ${currentOrderBy === opt && "focus"}`}
                style={{ width: "100%", textAlign: "left" }}
                onClick={() => {
                  setCurrentFilters((prev) => ({
                    ...prev,
                    sortBy: [
                      {
                        property: opt,
                        sortDirection: currentOrderByDirection || "ASC",
                      },
                    ],
                  }));
                }}
              >
                {opt as string}
              </button>
            </li>
          ))}
        </div>
      )}
    </FloatingDiv>
  );

  // return (
  //   <MoreDropdown
  //     wrapperCls="btn-group"
  //     drop={drop}
  //     btn="btn btn-default"
  //     title="Sorting"
  //     button={(onDropDownToggle, disabled) => (
  //       <>
  //         <button
  //           className={`${btnCls} ${disabled ? "disabled" : ""}`}
  //           onClick={onDropDownToggle}
  //           title="Sorting options"
  //         >
  //           {`${currentOrderBy ? `Sort by: ${currentOrderBy.replaceAll("_", " ")}` : "Sort by"}`}
  //         </button>
  //         <button
  //           className={`${btnCls} dropdown-toggle ${disabled ? "disabled" : ""}`}
  //           onClick={() => {
  //             setCurrentFilters((prev) => ({
  //               ...prev,
  //               orderBy: currentOrderBy + `${currentOrderByDirection === "ASC" ? "_DESC" : "_ASC"}`,
  //             }));
  //           }}
  //           title="Sort direction"
  //           disabled={!currentOrderBy}
  //           style={{ display: "flex", alignItems: "center" }}
  //         >
  //           {currentOrderByDirection === "DESC"
  //             ? Icon({ name: "sortDesc", size: 16 })
  //             : Icon({ name: "sortAsc", size: 16 })}
  //           <span className="sr-only">Sort direction</span>
  //         </button>
  //       </>
  //     )}
  //   >
  //     <div style={listContainerStyle}>
  //       {options?.map((opt, index) => (
  //         <li key={index}>
  //           <button
  //             className={`btn btn-ghost-secondary ${currentOrderBy === opt && "focus"}`}
  //             onClick={() => {
  //               setCurrentFilters((prev) => ({ ...prev, orderBy: opt + "_" + currentOrderByDirection }));
  //             }}
  //           >
  //             {opt.replaceAll("_", " ")}
  //           </button>
  //         </li>
  //       ))}
  //     </div>
  //   </MoreDropdown>
  // );
};
